import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { AlertCircleIcon, ClockIcon } from 'lucide-react';
import { Disparity } from '../../../types/Disparity/Disparity.type.ts';
import { DisparityCalculationStatus } from '../../../types/enums/DisparityCalculationStatus.enum.ts';
import { DisparitySubType as DisparitySubTypeEnum } from '../../../types/enums/DisparitySubType.enum.ts';
import { getDisparityColor } from '../../../utils/disparityColor.ts';
import EnhancementBadge from '../EnhancementBadge';

export interface DisparitySelectionItemProps {
  caseDisparity: Disparity;
  selectedDisparityId: number | null;
  onDisparitySelect: (disparityId: number) => void;
  updateDisparityMutation: (data: { disparityId: number; pinned: boolean }) => void;
  isUpdatingDisparity?: boolean;
  hidden?: boolean;
}

function DisparitySelectionItem({
  caseDisparity,
  selectedDisparityId,
  onDisparitySelect,
  updateDisparityMutation,
  isUpdatingDisparity,
  hidden,
}: DisparitySelectionItemProps) {
  const handleCheckboxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!caseDisparity.pinned) {
      onDisparitySelect(caseDisparity.id);
    }
  };

  const isEnhancement = caseDisparity.subtype?.id === DisparitySubTypeEnum.ENHANCEMENT;
  const enhancementStatute = isEnhancement
    ? caseDisparity.case.charge_enhancements
        ?.flat()
        .find((s) => s.is_charge_enhancement && caseDisparity.subtitle.includes(s.display_name))
    : null;

  return (
    <Box sx={{ position: 'relative', mt: isEnhancement ? '13px' : 0 }}>
      {isEnhancement && (
        <EnhancementBadge
          label={enhancementStatute?.display_name ?? 'Enhancement'}
          sx={{ position: 'absolute', top: -13, left: 14, zIndex: 1 }}
        />
      )}
      <Box
        key={caseDisparity.id}
        data-tour-disparity-item
        onClick={() => onDisparitySelect(caseDisparity.id)}
        sx={{
          opacity: hidden ? 0.5 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e2e8f0',
          borderColor: selectedDisparityId === caseDisparity.id ? 'accentBlue.main' : '#e2e8f0',
          borderWidth: selectedDisparityId === caseDisparity.id ? '2px' : '1px',
          cursor: 'pointer',
          borderRadius: '6px',
          paddingX: '20px',
          paddingY: '28px',
          pointerEvents:
            caseDisparity.calculation_status !== null &&
            caseDisparity.calculation_status?.id !== DisparityCalculationStatus.COMPLETED &&
            caseDisparity.calculation_status?.id !== DisparityCalculationStatus.FAILED
              ? 'none'
              : 'auto',
        }}
      >
        <FormControlLabel
          control={
            isUpdatingDisparity ? (
              <CircularProgress size={26} sx={{ marginX: '10px' }} />
            ) : (
              <Checkbox
                onChange={() =>
                  updateDisparityMutation({ disparityId: caseDisparity.id, pinned: !caseDisparity.pinned })
                }
                onClick={handleCheckboxClick}
                checked={caseDisparity.pinned}
                sx={{ margin: 0, '&.Mui-checked': { color: 'accentBlue.main' } }}
              />
            )
          }
          label={
            <Box onClick={(e) => e.preventDefault()}>
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                {caseDisparity.title}
              </Typography>
              <Typography variant='body2' sx={{ fontSize: '12px', color: '#718096' }}>
                Statute: {caseDisparity.subtitle}
              </Typography>
            </Box>
          }
        />
        {caseDisparity.calculation_status === null ||
        caseDisparity.calculation_status?.id === DisparityCalculationStatus.COMPLETED ? (
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              color: getDisparityColor(Number(caseDisparity.score)),
            }}
          >
            {caseDisparity.score?.toFixed(1) || 'N/A'}
          </Typography>
        ) : (
          <>
            {caseDisparity.calculation_status?.id === DisparityCalculationStatus.IN_PROGRESS && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <CircularProgress size={22} />
                <Typography variant='caption' sx={{ fontSize: '10px', color: 'primary.main', textAlign: 'center' }}>
                  In Progress
                </Typography>
              </Box>
            )}
            {caseDisparity.calculation_status?.id === DisparityCalculationStatus.PENDING && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'accentBlue.main',
                }}
              >
                <ClockIcon size={22} />
                <Typography variant='caption' sx={{ fontSize: '10px' }}>
                  Pending
                </Typography>
              </Box>
            )}
            {caseDisparity.calculation_status?.id === DisparityCalculationStatus.FAILED && (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: 'error.main' }}
              >
                <AlertCircleIcon size={22} />
                <Typography variant='caption' sx={{ fontSize: '10px' }}>
                  Failed
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default DisparitySelectionItem;
